// HTTP client com JWT bearer + auto-refresh + 401 -> logout.
// Centralizado: queries TanStack consomem este `api`.
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  updateAccessOnly,
} from "./auth-store";
import type { ApiErrorPayload, RefreshResponse } from "./types";

const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  "https://vitrine-api.hiitfitwear.com";

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _skipAuth?: boolean;
}

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const cfg = config as RetriableConfig;
  if (cfg._skipAuth) return cfg;
  const tok = getAccessToken();
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  return cfg;
});

// In-flight refresh promise pra coalescer chamadas concorrentes
let refreshing: Promise<string | null> | null = null;

export async function refreshAccess(): Promise<string | null> {
  const rt = getRefreshToken();
  if (!rt) return null;
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const r = await axios.post<RefreshResponse>(
        `${BASE_URL}/auth/refresh`,
        { refresh_token: rt },
        { timeout: 10_000 },
      );
      updateAccessOnly(r.data.access_token, r.data.refresh_token);
      return r.data.access_token;
    } catch {
      clearSession();
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<ApiErrorPayload>) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // 401 nao em /auth/* + nao ja retried => tenta refresh + replay
    if (
      status === 401 &&
      original &&
      !original._retry &&
      !original._skipAuth &&
      !original.url?.startsWith("/auth/")
    ) {
      original._retry = true;
      const newTok = await refreshAccess();
      if (newTok) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newTok}`;
        return api.request(original);
      }
      // refresh falhou — sessao morta
      clearSession();
      if (window.location.pathname !== "/login") {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/login?next=${next}`);
      }
    }
    return Promise.reject(error);
  },
);

// Helper de erro humanizado
export function apiErrorMessage(err: unknown): string {
  if (axios.isAxiosError<ApiErrorPayload>(err)) {
    const data = err.response?.data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

// Wrapper minimal sem auth (login)
export function postUnauth<T>(url: string, body: unknown, config?: AxiosRequestConfig): Promise<T> {
  return api
    .post<T>(url, body, { ...config, _skipAuth: true } as AxiosRequestConfig & { _skipAuth: true })
    .then((r) => r.data);
}

export { BASE_URL };
